"use client";

import { useMemo } from 'react'
import {
  Bar,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
} from 'recharts'
import { useFinance, useMonthlyAgg, useYearlyAgg, useTagPolicy, isIncome, monthlyWindow } from '@/lib/finance/finance-data-context'
import { currentMonthKey, fmtUsd, fmtUsdCompact, fmtPct } from '@/lib/finance/format'
import { Card, PageHeader, StatTile, LegendKey, Segmented } from '@/components/finance/ui'
import { usePageRange } from '@/components/finance/RangePicker'
import { ChartTooltip, barCursor } from '@/components/finance/ChartTooltip'
import { xAxisProps, yAxisProps, gridProps, BAR_MAX, BAR_RADIUS, CHART_MARGIN, ACTIVE_DOT } from '@/components/finance/chartDefaults'
import { SpendingDonut } from '@/components/finance/SpendingDonut'
import { useSessionState } from '@/lib/finance/usePersistedState'
import { bucketOf, fmtBucket, fmtBucketShort } from '@/lib/finance/buckets'

// Cash flow is a monthly statement, so density starts at month (no day/week);
// coarser buckets sum the months. Labels match the Trends density control.
type CashBucket = 'month' | 'quarter' | 'year'
const BUCKET_OPTIONS: { value: CashBucket; label: string }[] = [
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
]
const BUCKET_ADJ: Record<CashBucket, string> = { month: 'monthly', quarter: 'quarterly', year: 'yearly' }

export function CashFlow() {
  const monthly = useMonthlyAgg()
  const { txns } = useFinance()
  const { policy: tagPolicy } = useTagPolicy()

  // Ranges anchor to today; months without imported transactions show empty.
  const nowMonth = currentMonthKey()
  const dataStart = monthly.length ? monthly[0].month : nowMonth
  const { startMonth, endMonth, control } = usePageRange(dataStart, nowMonth)
  // Density override: month (default) / quarter / year. Session-scoped so it
  // survives a range change but resets on a fresh visit, like Trends.
  const [bucket, setBucket] = useSessionState<CashBucket>('fin.cashflow.bucket', 'month')
  // startMonth/endMonth are full-date bounds; iterate on their month prefixes.
  const startM = startMonth.slice(0, 7)
  const endM = endMonth.slice(0, 7)
  const rows = useMemo(() => {
    // Diverging chart: income sits above the zero line, spending mirrors below.
    // Coarser buckets sum the monthly statements into quarter/year columns.
    const win = monthlyWindow(monthly, startM, endM)
    const acc = new Map<string, { key: string; income: number; spending: number }>()
    for (const r of win) {
      const key = bucketOf(r.month, bucket)
      const b = acc.get(key) ?? { key, income: 0, spending: 0 }
      b.income += r.income
      b.spending += r.spending
      acc.set(key, b)
    }
    return [...acc.values()]
      .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
      .map((b) => ({ ...b, spendingNeg: -b.spending, net: b.income - b.spending }))
  }, [monthly, startM, endM, bucket])

  // Income broken down by category/source over the selected range — rollups
  // itemize only spending, so this scans raw income transactions.
  const incomeByCategory = useMemo(() => {
    const totals: Record<string, number> = {}
    for (const t of txns ?? []) {
      if (t.date < startMonth || t.date > endMonth) continue
      if (!isIncome(t, tagPolicy)) continue
      // Signed sum — a reversal in an income category nets against it.
      totals[t.category] = (totals[t.category] ?? 0) + t.amount
    }
    return totals
  }, [txns, startMonth, endMonth, tagPolicy])
  const hasIncomeBreakdown = Object.keys(incomeByCategory).length > 0

  const stats = useMemo(() => {
    if (!rows.length) return null
    const income = rows.reduce((s, r) => s + r.income, 0)
    const spending = rows.reduce((s, r) => s + r.spending, 0)
    const net = income - spending
    return {
      income,
      spending,
      net,
      avgIncome: income / rows.length,
      avgSpending: spending / rows.length,
      savingsRate: income > 0 ? net / income : 0,
    }
  }, [rows])

  // Annual statements are precomputed at import time (rollups.json).
  const yearly = useYearlyAgg()
  const annual = useMemo(
    () =>
      [...yearly]
        .sort((a, b) => (a.year < b.year ? 1 : -1))
        .map((r) => ({ ...r, rate: r.income > 0 ? r.net / r.income : 0 })),
    [yearly],
  )

  return (
    <div className="space-y-4">
      <PageHeader title="Cash flow">{control}</PageHeader>

      {stats && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatTile label="Total income" value={fmtUsdCompact(stats.income)} />
          <StatTile label="Total spending" value={fmtUsdCompact(stats.spending)} />
          <StatTile label="Net saved" value={fmtUsdCompact(stats.net)} delta={stats.net} deltaLabel="" />
          <StatTile label="Savings rate" value={fmtPct(stats.savingsRate)} />
        </div>
      )}

      <Card
        title={`Income vs spending · ${BUCKET_ADJ[bucket]}`}
        subtitle={`Average ${fmtUsd(stats?.avgIncome ?? 0)} in, ${fmtUsd(stats?.avgSpending ?? 0)} out · per ${bucket}`}
        actions={<Segmented ariaLabel="Density" options={BUCKET_OPTIONS} value={bucket} onChange={setBucket} />}
      >
        <div className="mb-2 flex flex-wrap gap-3">
          <LegendKey color="var(--series-2)" label="Income" />
          <LegendKey color="var(--series-6)" label="Spending" />
          <LegendKey color="var(--series-1)" label="Net" shape="line" />
        </div>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            {/* stackOffset="sign" anchors both bars of the shared column at
                zero: income (positive) fills 0→up in green, spending (negative)
                fills 0→down in red, and the net line passes through income −
                spending. Without it, the second bar of a stack is OFFSET by the
                first instead of starting at the axis. */}
            <ComposedChart data={rows} margin={CHART_MARGIN} stackOffset="sign">
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="key" {...xAxisProps} tickFormatter={(k: string) => fmtBucketShort(k, bucket)} minTickGap={32} />
              <YAxis {...yAxisProps} tickFormatter={(v: number) => fmtUsdCompact(Math.abs(v))} />
              <Tooltip cursor={barCursor} content={<ChartTooltip labelFormatter={(k) => fmtBucket(k, bucket)} />} />
              <ReferenceLine y={0} stroke="var(--baseline)" />
              <Bar dataKey="income" name="Income" stackId="cf" fill="var(--series-2)" maxBarSize={BAR_MAX} radius={BAR_RADIUS} />
              <Bar dataKey="spendingNeg" name="Spending" stackId="cf" fill="var(--series-6)" maxBarSize={BAR_MAX} radius={BAR_RADIUS} />
              <Line       type="monotone"
                dataKey="net"
                name="Net"
                stroke="var(--series-1)"
                strokeWidth={2}
                dot={false}
                activeDot={ACTIVE_DOT}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {hasIncomeBreakdown && (
        <Card title="Income by source" subtitle="Where income came from over the selected range">
          <SpendingDonut byGroup={incomeByCategory} fold={false} label="Income" />
        </Card>
      )}

      <Card title="By year">
        <div className="overflow-x-auto">
          <table className="w-full min-w-96 text-sm">
            <thead className="text-left text-xs text-ink-3">
              <tr>
                <th className="py-1.5 pr-4 font-medium">Year</th>
                <th className="py-1.5 pr-4 text-right font-medium">Income</th>
                <th className="py-1.5 pr-4 text-right font-medium">Spending</th>
                <th className="py-1.5 pr-4 text-right font-medium">Net</th>
                <th className="py-1.5 text-right font-medium">Savings rate</th>
              </tr>
            </thead>
            <tbody>
              {annual.map((r) => (
                <tr key={r.year} className="border-t border-edge">
                  <td className="py-1.5 pr-4 font-medium">{r.year}</td>
                  <td className="py-1.5 pr-4 text-right tabular-nums">{fmtUsd(r.income)}</td>
                  <td className="py-1.5 pr-4 text-right tabular-nums">{fmtUsd(r.spending)}</td>
                  <td className={`py-1.5 pr-4 text-right font-medium tabular-nums ${r.net >= 0 ? 'text-pos' : 'text-neg'}`}>
                    {fmtUsd(r.net)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{fmtPct(r.rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
